import { useState } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { submitFeedback, type FeedbackRating } from '../api/conversation';

/**
 * Thumbs up / down on an assistant answer. Optimistic: the button
 * fills immediately and reverts if the POST fails. Clicking the active
 * thumb again clears the rating. The rating is written onto the turn's
 * chat-log doc server-side, where the Insights aggregator counts it.
 *
 * Icon-only with English tooltips for now; localising the two labels is
 * a follow-up (it means adding keys to the platform-ui i18n catalogue).
 */
export function MessageFeedback({ messageId }: { messageId: string }) {
  const [rating, setRating] = useState<FeedbackRating | null>(null);
  const [busy, setBusy] = useState(false);

  async function choose(next: FeedbackRating) {
    if (busy) return;
    const value: FeedbackRating | '' = rating === next ? '' : next;
    const optimistic = value === '' ? null : value;
    const prev = rating;
    setRating(optimistic);
    setBusy(true);
    try {
      await submitFeedback(messageId, value);
    } catch {
      setRating(prev); // revert on failure
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="message-feedback" role="group" aria-label="Rate this answer">
      <button
        type="button"
        className={'message-feedback-btn' + (rating === 'up' ? ' is-active' : '')}
        onClick={() => choose('up')}
        disabled={busy}
        title="Helpful"
        aria-label="Helpful"
        aria-pressed={rating === 'up'}
      >
        <ThumbsUp size={13} />
      </button>
      <button
        type="button"
        className={'message-feedback-btn' + (rating === 'down' ? ' is-active is-down' : '')}
        onClick={() => choose('down')}
        disabled={busy}
        title="Not helpful"
        aria-label="Not helpful"
        aria-pressed={rating === 'down'}
      >
        <ThumbsDown size={13} />
      </button>
    </div>
  );
}
